import { Equipment } from '../../equipment';
import { Element } from '../element';
import { EquipmentElements } from '../equipmentElements';
import { Figure } from '../../figures/figure';
import { getElementFillColor, getElementOuterFillColor } from '../../elementFillColor';
import { BarrierSide, getFreeSidesForBarrier } from './barrier';
import { ExtensionType } from './extension';

export enum BrandingFit {
	Fit = 'fit',
	Fill = 'fill',
	Manual = 'manual'
}

export enum BrandingWallSide {
	Inner = 'inner',
	Outer = 'outer'
}

export interface BrandingImage {
	image: string;
	x: number;
	y: number;
	width: number;
	height: number;
	shapeIndex: number;
}

export interface BrandingParams {
	rotation: number;
	opacity: number;
	fit: BrandingFit;
	images: Array<BrandingImage>;
	showInner: boolean;
	showOuter: boolean;
}

export interface BrandingFace {
	shapeIndex: number;
	width: number;
	height: number;
	color: string;
	side?: BrandingWallSide;
}

export interface BrandingStyle {
	rotation: number;
	opacity: number;
	fit: BrandingFit;
}

export interface BrandingSides {
	showInner: boolean;
	showOuter: boolean;
}

export interface BrandingSize {
	width: number;
	height: number;
}

export interface BrandingContour {
	width: number;
	depth: number;
	leftRadius: number;
	rightRadius: number;
}

export interface BrandingPlacement {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface BrandingCanvasRect {
	x: number;
	y: number;
	width: number;
	height: number;
	offsetX: number;
	offsetY: number;
}

export const BRANDING_DEFAULT_OPACITY = 1;
export const BRANDING_DEFAULT_TRANSPARENCY = 0;
export const BRANDING_DEFAULT_ROTATION = 0;
export const BRANDING_DEFAULT_SHOW_INNER = true;
export const BRANDING_DEFAULT_SHOW_OUTER = true;
export const BRANDING_MAX_FILE_SIZE = 80 * 1024 * 1024;
export const BRANDING_FILE_TYPE = ['image/png', 'image/svg+xml', 'image/jpeg'];
export const BRANDING_FILE_EXTENSION = ['.png', '.jpg', '.jpeg', '.svg'];
const BRANDING_IMAGE_CACHE_LIMIT = 16;

export const SIDE_WALL_OUTER_FACE = 0;
export const SIDE_WALL_INNER_FACE = 1;

const TRAPEZE_SIDE_WALL_WEDGE_KROMKA_INSET_PX = 1;

/** 2D trapeze side-wall wedge: clip, placement, compression, and which branding face to use. */
export function getTrapezeSideWallWedgeLayout(
	slopeTowardViewer: boolean,
	isLeftWall: boolean,
	innerWalls: boolean,
	panelWidthPx: number,
	wedgeWidthM: number,
	faceDepthM: number
) {
	const inset = TRAPEZE_SIDE_WALL_WEDGE_KROMKA_INSET_PX;
	const panelAdjacent = isLeftWall === innerWalls;
	const compression =
		Number.isFinite(wedgeWidthM) && Number.isFinite(faceDepthM) && faceDepthM > 0
			? wedgeWidthM / faceDepthM
			: 1;

	return {
		side: slopeTowardViewer ? BrandingWallSide.Inner : BrandingWallSide.Outer,
		shapeIndex: slopeTowardViewer ? SIDE_WALL_INNER_FACE : SIDE_WALL_OUTER_FACE,
		clipX: panelAdjacent ? 0 : inset,
		widthReduce: inset,
		groupX: panelAdjacent ? 0 : panelWidthPx,
		scaleFromOuter: isLeftWall ? !innerWalls : innerWalls,
		compression
	};
}

const ELEMENTS_WHITELIST = [
	EquipmentElements.BaseRectangle,
	EquipmentElements.BaseTrapeze,
	EquipmentElements.BackWall,
	EquipmentElements.CentralPillar,
	EquipmentElements.SideWalls,
	EquipmentElements.Shelf,
	EquipmentElements.AngleShelf
];

const loadedBrandingImages = new Map<string, HTMLImageElement>();
const pendingBrandingImages = new Map<string, Promise<HTMLImageElement>>();

export function getBrandingFileExtension(name: string) {
	const trimmed = name.trim();
	const dot = trimmed.lastIndexOf('.');

	if (dot < 0) {
		return '';
	}

	return `.${trimmed.slice(dot + 1).toLowerCase()}`;
}

export function isBrandingFileAllowed(file: File) {
	const type = (file.type || '').toLowerCase();
	const extension = getBrandingFileExtension(file.name || '');

	return BRANDING_FILE_TYPE.includes(type) || BRANDING_FILE_EXTENSION.includes(extension);
}

export function isBrandingSvgFile(file: File) {
	return (file.type || '').toLowerCase() === 'image/svg+xml' || getBrandingFileExtension(file.name || '') === '.svg';
}

function isSafeBrandingSvg(text: string) {
	const parsed = new DOMParser().parseFromString(text, 'image/svg+xml');

	if (parsed.querySelector('parsererror')) {
		return false;
	}

	const root = parsed.documentElement;

	if (!root || root.nodeName.toLowerCase() !== 'svg') {
		return false;
	}

	if (parsed.querySelector('script, foreignObject')) {
		return false;
	}

	return !/\bon[a-z]+\s*=/i.test(text) && !/javascript:/i.test(text) && !/<script[\s>]/i.test(text);
}

export async function validateBrandingFile(file: File): Promise<'format' | 'size' | null> {
	if (file.size > BRANDING_MAX_FILE_SIZE) {
		return 'size';
	}

	if (!isBrandingFileAllowed(file)) {
		return 'format';
	}

	if (!isBrandingSvgFile(file)) {
		return null;
	}

	return isSafeBrandingSvg(await file.text()) ? null : 'format';
}

export function isBrandingAllowed(type: EquipmentElements) {
	return ELEMENTS_WHITELIST.includes(type);
}

export type BrandingMeshFace = 'front' | 'back';

const BRANDING_FRONT_ONLY_VIEW = [
	EquipmentElements.BaseTrapeze,
	EquipmentElements.BackWall,
	EquipmentElements.Shelf,
	EquipmentElements.AngleShelf
] as const;

export function getBrandingEquipmentViewSides(element: Element, equipment: Equipment | null): Array<BarrierSide> {
	if (element.parent.is(EquipmentElements.BaseRectangle)) {
		if (!equipment) {
			return [BarrierSide.Front];
		}

		const sides = getFreeSidesForBarrier(element, equipment);

		return sides.length ? sides : [BarrierSide.Front];
	}

	if (element.parent.is(EquipmentElements.CentralPillar)) {
		return [BarrierSide.Front, BarrierSide.Back];
	}

	if (element.parent.is(...BRANDING_FRONT_ONLY_VIEW)) {
		return [BarrierSide.Front];
	}

	return [BarrierSide.Front];
}

export function shouldRenderBrandingOnEquipmentView(
	element: Element,
	equipment: Equipment | null,
	flip: boolean
): boolean {
	const facing = flip ? BarrierSide.Back : BarrierSide.Front;

	return getBrandingEquipmentViewSides(element, equipment).includes(facing);
}

export function getBrandingMeshFaces(element: Element, equipment: Equipment | null): Array<BrandingMeshFace> {
	if (element.parent.is(EquipmentElements.BaseRectangle)) {
		if (!equipment) {
			return ['front'];
		}

		const sides = getFreeSidesForBarrier(element, equipment);
		const faces: Array<BrandingMeshFace> = ['front'];

		if (sides.includes(BarrierSide.Back)) {
			faces.push('back');
		}

		return faces;
	}

	if (element.parent.is(EquipmentElements.CentralPillar)) {
		return ['front', 'back'];
	}

	return ['front'];
}

export function getClearedBrandingStyle(): BrandingStyle {
	return {
		rotation: BRANDING_DEFAULT_ROTATION,
		opacity: BRANDING_DEFAULT_OPACITY,
		fit: BrandingFit.Fit
	};
}

export function clampBrandingOpacity(opacity: number) {
	if (!Number.isFinite(opacity)) {
		return BRANDING_DEFAULT_OPACITY;
	}

	return Math.min(1, Math.max(0, opacity));
}

export function opacityToTransparencyPercent(opacity: number) {
	return Math.round((1 - clampBrandingOpacity(opacity)) * 100);
}

export function transparencyPercentToOpacity(percent: number) {
	if (!Number.isFinite(percent)) {
		return BRANDING_DEFAULT_OPACITY;
	}

	return clampBrandingOpacity(1 - percent / 100);
}

export function isBrandingSideEnabled(params: Partial<BrandingSides> | undefined, side?: BrandingWallSide) {
	if (side === BrandingWallSide.Inner) {
		return params?.showInner !== false;
	}

	if (side === BrandingWallSide.Outer) {
		return params?.showOuter !== false;
	}

	return true;
}

export function getBrandingSideLabelKey(side: BrandingWallSide) {
	if (side === BrandingWallSide.Outer) {
		return 'EQUIPMENT_EDIT.CONFIGURATION_PARAMS.BRANDING.SIDE_OUTER';
	}

	return 'EQUIPMENT_EDIT.CONFIGURATION_PARAMS.BRANDING.SIDE_INNER';
}

export function findBrandingImage(images: Array<BrandingImage>, shapeIndex: number): BrandingImage | undefined {
	const match = images.find((image) => image.shapeIndex === shapeIndex);

	if (match || images.length !== 1) {
		return match;
	}

	return images[0];
}

export function mapBrandingToFaceSize(image: BrandingImage, source: BrandingFace, width: number, height: number): BrandingImage {
	const scaleX = width / (source.width || 1);
	const scaleY = height / (source.height || 1);

	if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY)) {
		return image;
	}

	return {
		...image,
		x: image.x * scaleX,
		y: image.y * scaleY,
		width: image.width * scaleX,
		height: image.height * scaleY
	};
}

export function getBrandingFaces(element: Element): Array<BrandingFace> {
	if (!isBrandingAllowed(element.parent.type)) {
		return [];
	}

	if (element.parent.is(EquipmentElements.SideWalls)) {
		const shape: Figure | undefined = element.shapes[0];

		if (!shape) {
			return [];
		}

		const width = shape.depth ?? 0;
		const height = shape.height ?? 0;

		return [
			{
				shapeIndex: SIDE_WALL_OUTER_FACE,
				side: BrandingWallSide.Outer,
				width,
				height,
				color: getElementOuterFillColor(element, shape)
			},
			{
				shapeIndex: SIDE_WALL_INNER_FACE,
				side: BrandingWallSide.Inner,
				width,
				height,
				color: getElementFillColor(element, shape)
			}
		];
	}

	return element.shapes.map((shape, index) => ({
		shapeIndex: index,
		width: shape.frontWidth ?? shape.width ?? 0,
		height: shape.height ?? 0,
		color: getElementFillColor(element, shape)
	}));
}

function pickCoverSize(face: BrandingFace, byWidth: BrandingSize, byHeight: BrandingSize, fit: BrandingFit): BrandingSize {
	if (fit === BrandingFit.Fill) {
		return byWidth.height >= face.height ? byWidth : byHeight;
	}

	return byWidth.height <= face.height ? byWidth : byHeight;
}

export function getBrandingPlacement(face: BrandingFace, aspect: number, fit: BrandingFit) {
	const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
	const byWidth: BrandingSize = { width: face.width, height: face.width / safeAspect };
	const byHeight: BrandingSize = { width: face.height * safeAspect, height: face.height };
	const size = pickCoverSize(face, byWidth, byHeight, fit);

	return {
		x: (face.width - size.width) / 2,
		y: (face.height - size.height) / 2,
		...size
	};
}

export function clampBrandingPlacement(face: BrandingFace, placement: BrandingPlacement) {
	const maxWidth = Math.max(face.width, 0);
	const maxHeight = Math.max(face.height, 0);
	let width = Math.max(0, placement.width);
	let height = Math.max(0, placement.height);

	if (width > maxWidth || height > maxHeight) {
		const scale = Math.min(maxWidth / (width || 1), maxHeight / (height || 1));

		width *= scale;
		height *= scale;
	}

	return {
		x: Math.min(Math.max(placement.x, 0), Math.max(0, maxWidth - width)),
		y: Math.min(Math.max(placement.y, 0), Math.max(0, maxHeight - height)),
		width,
		height
	};
}

export function adaptBrandingImages(element: Element) {
	const branding = element.getExtension<BrandingParams>(ExtensionType.Branding);

	if (!branding?.params?.images?.length) {
		return null;
	}

	const faces = getBrandingFaces(element);
	const { fit } = branding.params;

	return branding.params.images.map((image) => {
		const face = faces.find(({ shapeIndex }) => shapeIndex === image.shapeIndex);

		if (!face) {
			return image;
		}

		const aspect = image.height ? image.width / image.height : 1;

		if (fit === BrandingFit.Fit || fit === BrandingFit.Fill) {
			return { ...image, ...getBrandingPlacement(face, aspect, fit) };
		}

		return { ...image, ...clampBrandingPlacement(face, image) };
	});
}

// Model x/y = bottom-left on face; Konva position/offset use image center as rotation pivot.
export function brandingToCanvas(image: BrandingPlacement, scale: number, faceHeightPx: number): BrandingCanvasRect {
	const width = image.width * scale;
	const height = image.height * scale;
	const bottomLeftX = image.x * scale;
	const bottomLeftY = faceHeightPx - image.y * scale;

	return {
		x: bottomLeftX + width / 2,
		y: bottomLeftY - height / 2,
		width,
		height,
		offsetX: width / 2,
		offsetY: height / 2
	};
}

// x/y are Konva node center; returns placement with bottom-left in face coordinates.
export function brandingFromCanvas(
	x: number,
	y: number,
	width: number,
	height: number,
	scale: number,
	faceHeightPx: number
): BrandingPlacement {
	const bottomLeftX = x - width / 2;
	const bottomLeftY = y + height / 2;

	return {
		x: bottomLeftX / scale,
		y: (faceHeightPx - bottomLeftY) / scale,
		width: width / scale,
		height: height / scale
	};
}

function rememberBrandingImage(source: string, image: HTMLImageElement) {
	loadedBrandingImages.delete(source);
	loadedBrandingImages.set(source, image);

	while (loadedBrandingImages.size > BRANDING_IMAGE_CACHE_LIMIT) {
		const oldest = loadedBrandingImages.keys().next().value;

		if (!oldest) {
			break;
		}

		loadedBrandingImages.delete(oldest);
	}
}

export function unloadBrandingImage(source: string) {
	loadedBrandingImages.delete(source);
	pendingBrandingImages.delete(source);
}

export function loadBrandingImage(source: string) {
	const loaded = loadedBrandingImages.get(source);

	if (loaded) {
		rememberBrandingImage(source, loaded);

		return Promise.resolve(loaded);
	}

	const pending =
		pendingBrandingImages.get(source) ||
		new Promise<HTMLImageElement>((resolve, reject) => {
			const image = new Image();

			if (/^https?:/i.test(source)) {
				image.crossOrigin = 'anonymous';
			}

			image.addEventListener('load', () => {
				rememberBrandingImage(source, image);
				resolve(image);
			});
			image.addEventListener('error', () => reject(new Error('branding image is not readable')));
			image.src = source;
		}).finally(() => pendingBrandingImages.delete(source));

	pendingBrandingImages.set(source, pending);

	return pending;
}
